// This file contains the different dropdown components used in the filters section.
// It is used in relative filter and search condition filters.

import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import { fontSize, palette } from '../..';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PatternCollectionType } from '../ChartFieldFilter/UserFilterCardInterface';

interface IDropDownForPatternProps {
  items: PatternCollectionType[];
  exprType?: string;
  value: string;
  handleDropDownForPatternOnChange: (e: any) => void;
  usersx?: any;
  from?: string;

  color?: string;
}

export const DropDownForPattern = ({
  items,
  value,
  exprType = 'exprType',
  usersx,
  handleDropDownForPatternOnChange,
  from = 'chartFilter',
  color,
}: IDropDownForPatternProps) => {
  return (
    <FormControl
      fullWidth
      size="small"
      sx={{
        ...dropDownStyles.customSelect,
        '& .MuiOutlinedInput-root': {
          borderRadius: '0px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: color,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: color,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: color,
          },
        },
      }}
    >
      <Select
        sx={{
          height: '1.5rem',
          fontSize: fontSize.large,
          textAlign: 'left',
          '.MuiSelect-icon': from !== 'datasetFilter' && {
            fontSize: fontSize.TripleExtraLarge,
            marginLeft: '20px',
            color: color || palette.primary.contrastText,
            ...usersx,
          },
        }}
        IconComponent={KeyboardArrowDownIcon}
        onChange={(e) => {
          handleDropDownForPatternOnChange(e);
        }}
        value={value || 'greaterThan'}
        renderValue={(selectedValue) => {
          const selectedItem = items.find((item) => item.key === selectedValue);
          return (
            <Typography sx={{ color: color, fontSize: '12px' }}>
              {selectedItem ? selectedItem.value : ''}
            </Typography>
          );
        }}
      >
        {items.map((item: any) => {
          return (
            <MenuItem
              key={item.key}
              value={item.key}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(43, 185, 187, 0.1)', // Change background color for selected option
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(43, 185, 187, 0.2)', // Change hover background color
                },
              }}
            >
              <Typography
                sx={{
                  width: 'auto',
                  minWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '12px',
                }}
              >
                {item.value}
              </Typography>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

interface IDropDownForDatePatternProps {
  items: PatternCollectionType[];
  handleDropDownForDatePatternOnChange: (e: any) => void;
  usersx?: any;
  value: string;
  from?: string;

  color?: string;
}

///Dropdown list to select Time grain
export const DropDownForDatePattern = ({
  items,
  usersx,
  handleDropDownForDatePatternOnChange,
  value,
  color,
  from = 'chartFilter',
}: IDropDownForDatePatternProps) => {
  return (
    <FormControl
      fullWidth
      size="small"
      variant="outlined"
      sx={{
        ...dropDownStyles.customSelect,
        '& .MuiOutlinedInput-root': {
          borderRadius: '0px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: color,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: color,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: color,
          },
        },
      }}
    >
      <Select
        sx={{
          height: '1.5rem',
          fontSize: fontSize.large,
          textAlign: 'left',
          '.MuiSelect-icon': from !== 'datasetFilter' && {
            fontSize: fontSize.TripleExtraLarge,
            marginLeft: '20px',
            color: color || palette.primary.contrastText,
            right: from === 'reportFilter' ? '4px' : '1px',
            ...usersx,
          },
        }}
        IconComponent={KeyboardArrowDownIcon}
        onChange={(e) => {
          handleDropDownForDatePatternOnChange(e);
        }}
        value={value}
        renderValue={(selectedValue) => {
          const selectedItem = items.find((item) => item.key === selectedValue);
          return (
            <Typography sx={{ color: color, fontSize: '12px' }}>
              {selectedItem ? selectedItem.value : ''}
            </Typography>
          );
        }}
      >
        {items.map((item: any) => {
          return (
            <MenuItem
              key={item.key}
              value={item.key}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(43, 185, 187, 0.1)', // Change background color for selected option
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(43, 185, 187, 0.2)', // Change hover background color
                },
              }}
            >
              <Typography
                sx={{
                  minWidth: '100%',
                  width: 'auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '12px',
                  lineHeight: '20px',
                }}
              >
                {item.value}
              </Typography>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

interface IDropDownForRelativePatternProps {
  items: PatternCollectionType[];
  exprType?: string;
  handleDropDownForRelativePatternOnChange: (event: any, key: string) => Promise<void> | void;
  usersx?: any;
  filterFieldData?: any;
  from?: string;
  rest?: any;
}

export const DropDownForRelativePattern = ({
  items,
  exprType = 'exprType',
  usersx,
  handleDropDownForRelativePatternOnChange,
  filterFieldData,
  from = 'chartFilter',
  ...rest
}: IDropDownForRelativePatternProps) => {
  /// Get the value of the selected item from the dropdown
  const getValue = (type: string) => {
    if (from === 'datasetFilter' && filterFieldData.current?.relativeCondition) {
      if (type === 'from') {
        return filterFieldData.current.relativeCondition.from[2];
      }
      if (type === 'to') {
        return filterFieldData.current.relativeCondition.to[2];
      }
      if (type === 'anchorDate') {
        return filterFieldData.current.relativeCondition.anchorDate;
      }
      if (type === 'value_from') {
        return filterFieldData.current.relativeCondition.from[1];
      }
      if (type === 'value_to') {
        return filterFieldData.current.relativeCondition.to[1];
      }
      if (type === 'last_current_next_to') {
        return filterFieldData.current.relativeCondition.to[0];
      }
      if (type === 'last_current_next_from') {
        return filterFieldData.current.relativeCondition.from[0];
      }
    } else {
      return filterFieldData?.[type];
    }
    return '';
  };
  return (
    <FormControl fullWidth size="small">
      <Select
        {...rest}
        sx={{
          height: '1.5rem',
          fontSize: '14px',
          textAlign: 'left',
          '.MuiSelect-icon': from !== 'datasetFilter' && {
            fontSize: fontSize.TripleExtraLarge,
            marginLeft: '20px',
            marginRight: from === 'reportFilter' ? '0.1rem' : '',
            color: palette.primary.contrastText,
            ...usersx,
          },
        }}
        IconComponent={KeyboardArrowDownIcon}
        onChange={(e) => {
          handleDropDownForRelativePatternOnChange(e, exprType);
        }}
        value={getValue(exprType)}
      >
        {items.map((item: any) => {
          return (
            <MenuItem key={item.key} value={item.key}>
              <Typography
                sx={{
                  width: 'auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: fontSize.medium,
                  color: palette.primary.contrastText,
                }}
              >
                {item.value}
              </Typography>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

///Style for changing border color of different menus on focus
export const dropDownStyles = {
  customSelect: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: '#2bb9bb',
      },
      '&:hover fieldset': {
        borderColor: '#2bb9bb',
      },
    },
  },
};
